import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  FormControlLabel,
  Button,
  TextField,
  Checkbox,
  Box,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material/";

import { debounce, flow, isArray, isNumber, isObject } from "lodash";

// ICONS
import {
  ArrowForwardIos,
  Autorenew,
  Close,
  Delete,
  Update,
} from "@mui/icons-material";

import LinearProgress from "@mui/material/LinearProgress";
import { CustomIcons, formatDate } from "../../Components/helpers/Helpers";
import { CustomButtonPlusIcon } from "../../Components/Common/Design/CustomButtonPlusIcon";
import {
  DocsContext,
  PermissionContext,
} from "../../Components/Context/Context";
import LoadingPage from "../../Components/Common/Loading/LoadingPage";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import api from "../../api/axios";
import { useNavigate } from "react-router-dom";
import { COLORS } from "./muiStyles/muiStyles";

// ======================================================
// COMPONENT: ConfigControlSection
// Descripción: Controla el progreso y actualización de
// estados de expediente, así como la eliminación de casos.
// ======================================================
const ConfigControlSection = ({
  dataCase,
  caseId,
  setDataCase,
  progress,
  onFinish,
  setUpdatedCase,
  updatedCase,
  glassSurface,
}) => {
  const navigate = useNavigate();

  const Swal = require("sweetalert2");

  const { can } = useContext(PermissionContext);
  const canChange = can("cases", "change");
  const canDelete = can("cases", "delete");
  // -------------------------------
  // ESTADOS LOCALES
  // -------------------------------
  const { docsInfo } = useContext(DocsContext);
  const [revertCaseFlow, setRevertCaseFlow] = useState(null);
  const [activeStepStatus, setActiveStepStatus] = useState(0);
  const [activeStepDelete, setActiveStepDelete] = useState(0);
  const [openModalStatus, setOpenModalStatus] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorDoc, setErrorDocs] = useState(false);
  const [docsDecision, setDocsDecision] = useState(0);
  const [signatureAction, setSignatureAction] = useState("");
  const [dateTrialTime, setDateTrialTime] = React.useState(null);

  const stepsChangeStatus = ["Inicio", "Finalizar"];
  const stepsDelete = ["Inicio", "Documentación", "Eliminar Expediente"];

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md")); // true si pantalla es md o menos

  const getNormalizedStatusName = (statusName) =>
    (statusName || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const normalizedSelectedStatus = getNormalizedStatusName(
    revertCaseFlow?.status_name,
  );

  const isPendingSignatureSelected = normalizedSelectedStatus.includes("firma");

  // FUNCION: Actualizar estados de peritaje y apud
  const handleConfigPanel = async () => {
    const urlEditState = `${process.env.REACT_APP_API_URL}/cases/updatecontrolpanel`;

    const formData = new FormData();
    formData.append("case_id", Number(caseId));
    formData.append(
      "apud_acta",
      dataCase.apud_acta == null ? null : Number(dataCase.apud_acta),
    );
    formData.append(
      "peritaje",
      dataCase.peritaje == null ? null : Number(dataCase.peritaje),
    );
    formData.append(
      "expiration",
      dataCase.expiration == null ? null : Number(dataCase.expiration),
    );

    // Confirmación
    const result = await Swal.fire({
      title: "¿Desea cambiar el estado?",
      text: "Esta acción actualizará la configuración del expediente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Sí, cambiar",
      cancelButtonText: "Volver",
    });

    if (!result.isConfirmed) return;

    // Loading
    Swal.fire({
      title: "Actualizando estado...",
      text: "Por favor espere",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await api.post(urlEditState, formData);

      Swal.fire({
        title: "¡Estado actualizado!",
        icon: "success",
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Aceptar",
      });
    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error",
        text: "No se pudo actualizar el estado. Inténtelo nuevamente.",
        icon: "error",
        confirmButtonColor: "#1ab394",
      });
    }
  };

  // FUNCION: Avanzar pasos de modal
  // flag = "status" o "eliminar"
  const handleNext = async (flag) => {
    setLoading(true);

    // ---------- CASO: Cambiar estado ----------
    if (flag === "status") {
      console.log("Entra", activeStepStatus);

      // Avanzar paso
      setActiveStepStatus((prev) =>
        Math.min(prev + 1, stepsChangeStatus.length - 1),
      );
      // console.log(revertCaseFlow);
      // console.log(dataCase.tasks);

      // Solo en el primer paso, hacer revert de status
      if (activeStepStatus === 0 && revertCaseFlow) {
        const tasksToDelete = [];
        let principalTask = null;

        // Filtrar tareas a eliminar y principal
        dataCase.tasks.forEach((task) => {
          if (task?.cases_flow_relation?.order > revertCaseFlow.order) {
            tasksToDelete.push(task.id);
          } else if (
            task?.cases_flow_relation?.order === revertCaseFlow.order
          ) {
            principalTask = task;
          }
        });

        const formData = new FormData();
        formData.append("tasks_to_delete", JSON.stringify(tasksToDelete));
        formData.append(
          "principal_task",
          principalTask ? JSON.stringify(principalTask) : "",
        );
        formData.append(
          "principal_task_flow_id",
          Number(revertCaseFlow?.id || 0),
        );
        formData.append("case_id", Number(caseId));

        if (isPendingSignatureSelected) {
          formData.append("signature_action", signatureAction);

          if (signatureAction === "keep") {
            formData.append(
              "signature_config",
              JSON.stringify(principalTask?.config ?? null),
            );
          }
        }

        console.log(tasksToDelete);
        try {
          const url = `${process.env.REACT_APP_API_URL}/casestasks/revertcasestatus`;
          const { data } = await api.post(url, formData);
          console.log(data);
          setRevertCaseFlow(null);
          setUpdatedCase((prev) => !prev);
        } catch (error) {
          console.error("Error al revertir estado del caso:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }

    // ---------- CASO: Eliminar ----------
    else if (flag === "eliminar") {
      console.log("Entra", activeStepDelete);

      // Avanzar paso
      setActiveStepDelete((prev) => Math.min(prev + 1, stepsDelete.length - 1));

      if (activeStepDelete === 0) {
        // Simular retraso antes de continuar
        setTimeout(() => {
          console.log("1S después");
          setLoading(false);
        }, 1000);
      } else if (activeStepDelete === 1) {
        console.log(typeof docsDecision, "docsDecision");

        if ([0, 1].includes(Number(docsDecision))) {
          console.log("Conservar doc");
          await handleDeleteCase(true);
          setUpdatedCase((prev) => !prev);
        } else if (docsDecision == 2) {
          console.log("Eliminar doc");

          const docsToDelete = deleteMultipleDocuments();

          if (docsToDelete) {
            console.log("Docs preparados para eliminar:", docsToDelete);
            const responseCase = await handleDeleteCase(true);
            setUpdatedCase((prev) => !prev);
            console.log("Case eliminado:", responseCase);
          } else {
            console.warn("No hay documentos para eliminar");
            setLoading(false);
          }
        }
      }
    }
  };

  // FUNCION: Eliminar caso
  const handleDeleteCase = async (flag = false) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("case_id", Number(caseId));
    formData.append("keep_doc", flag);

    if (flag) {
      const url = `${process.env.REACT_APP_API_URL}/cases/delete`;
      try {
        const response = await api.post(url, formData);
        setLoading(false);
        return response;
      } catch (error) {
        console.error(error);
      }
    }
  };

  // FUNCION: Preparar documentos para eliminación múltiple
  const deleteMultipleDocuments = async () => {
    setLoading(true);
    const allDocs = [];

    Object.entries(docsInfo).forEach(([caseId, categories]) => {
      Object.entries(categories).forEach(([category, docsArray]) => {
        docsArray.forEach((doc) => {
          allDocs.push({ doc_id: doc.doc_id || null, path: doc.path });
        });
      });
    });

    // console.log(allDocs);

    if (allDocs?.length > 0) {
      const formData = new FormData();
      formData.append("documents", JSON.stringify(allDocs));

      try {
        const { data } = await api.post(
          `${process.env.REACT_APP_API_URL}/s3/destroymultiple`,
          formData,
        );

        Swal.close();
        if (data.success) {
          setLoading(false);
          return data.success;
        } else if (data?.error?.errorInfo?.length > 0) setErrorDocs(true);
      } catch (error) {
        Swal.close();
        Swal.fire({
          title: "¡Error de conexión!",
          icon: "error",
          text: "No se pudo eliminar los archivos.",
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Continuar",
        });
        console.error(error);
      }
    }
  };

  // FUNCIONES: Retroceder pasos
  const handleBackDelete = () => {
    setActiveStepDelete((prev) => Math.max(prev - 1, 0));
    setDocsDecision(0);
  };
  const handleBackStatus = () =>
    setActiveStepStatus((prev) => Math.max(prev - 1, 0));

  // FUNCION: Finalizar modal
  const handleFinish = (flag) => {
    if (flag == "delete") navigate("/expedientes");
    setUpdatedCase((prev) => !prev);

    handleCloseModal();
    onFinish && onFinish();
  };

  // FUNCIONES: Abrir / cerrar modales
  const handleOpenModal = (flag) => {
    if (flag == "status") {
      if (revertCaseFlow?.id) {
        setSignatureAction("");
        setOpenModalStatus(true);
      } else {
        Swal.fire({
          title: "¡Error!",
          icon: "error",
          text: "Por favor seleccione un estado válido.",
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Volver",
          showDenyButton: false,
        });
      }
    } else if (flag == "eliminar") {
      setOpenModalDelete(true);
    }
  };

  const handleCloseModal = () => {
    setOpenModalStatus(false);
    setOpenModalDelete(false);
    setDocsDecision(0);
    setSignatureAction("");
    setActiveStepStatus(0);
    setActiveStepDelete(0);
    setRevertCaseFlow(null);
  };

  const filteredOldStates = React.useMemo(() => {
    const completeFlow = dataCase?.completeFlow || [];
    const caseTasks = dataCase?.tasks || [];

    // Mostramos todos los estados que tengan task asociada en el expediente.
    const taskIds = new Set(
      caseTasks.map((task) => Number(task?.task_id || 0)),
    );

    const availableStates = completeFlow
      .filter((flow) => taskIds.has(Number(flow?.id || 0)))
      .sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0));

    return [
      { id: 0, status_name: "Seleccionar", order: 0 },
      ...availableStates,
    ];
  }, [dataCase?.completeFlow, dataCase?.tasks]);

  const handleChangeTrialDate = async () => {
    if (!dateTrialTime) {
      Swal.fire({
        icon: "warning",
        title: "Fecha requerida",
        text: "Debes seleccionar una fecha y hora válida",
      });
      return;
    }

    // Convierte a string con fecha y hora completa
    const formattedDate = dateTrialTime.format("YYYY-MM-DD HH:mm:ss");

    const confirm = await Swal.fire({
      title: "¿Confirmar cambio?",
      text: `La fecha del juicio será actualizada a:\n${dateTrialTime.format(
        "DD/MM/YYYY HH:mm",
      )}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Sí, guardar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    try {
      // Loading
      Swal.fire({
        title: "Guardando fecha...",
        text: "Por favor espera",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      // Petición al backend enviando fecha y hora exacta
      await api.post("/cases/update", {
        id: Number(caseId),
        trial_date: formattedDate, // <-- aquí va la fecha y hora exacta
      });

      //  Éxito
      await Swal.fire({
        icon: "success",
        title: "Fecha actualizada",
        text: "La fecha del juicio se guardó correctamente",
        timer: 2000,
        showConfirmButton: false,
      });

      // Actualiza estado local si quieres reflejar el cambio inmediatamente
      setDataCase({
        ...dataCase,
        trial_date: formattedDate,
      });
    } catch (error) {
      console.error("Error al cambiar fecha de juicio:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          "No se pudo guardar la fecha del juicio",
      });
    }
  };

  // ======================================================
  // RENDER PRINCIPAL
  // ======================================================
  return (
    <Grid
      item
      container
      xs={12}
      md={12}
      m={"auto"}
      sx={{
        ...glassSurface,
        alignItems: "stretch",
        display: "flex",
        flexDirection: "row",
      }}
    >
      {/* PROGRESO */}

      {/* ---------------- SECCIÓN IZQUIERDA: Apud acta / Peritaje ---------------- */}
      <Grid item xs={12} lg={4} sx={{ p: { xs: "10px", md: "10px" } }}>
        {canChange && (
          <Box
            sx={{
              background: "rgba(255, 255, 255, 0.56)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              p: { xs: "10px" },
              borderRadius: "12px",
              border: "1px solid rgba(255, 255, 255, 0.55)",
              boxShadow: "0 6px 18px rgba(15,23,42,0.05)",
              height: "stretch",
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Configuración del expediente
            </Typography>

            <Divider sx={{ mb: 1.5 }} />

            {[
              { label: "¿Con Peritaje?", field: "peritaje" },
              { label: "¿Con Apud acta?", field: "apud_acta" },
              { label: "¿Con Vencimiento?", field: "expiration" },
            ].map((item) => {
              const rawValue = dataCase?.[item.field];
              const normalizedValue =
                rawValue === null || rawValue === undefined || rawValue === ""
                  ? null
                  : Number(rawValue);

              return (
                <Grid
                  container
                  alignItems="center"
                  justifyContent="space-between"
                  key={item.field}
                >
                  <Grid item xs={6}>
                    <Typography fontSize={13} fontWeight={500}>
                      {item.label}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={normalizedValue === 0}
                          onChange={() =>
                            setDataCase((prev) => {
                              const prevRaw = prev?.[item.field];
                              const prevNormalized =
                                prevRaw === null ||
                                prevRaw === undefined ||
                                prevRaw === ""
                                  ? null
                                  : Number(prevRaw);

                              return {
                                ...prev,
                                [item.field]: prevNormalized === 0 ? null : 0,
                              };
                            })
                          }
                        />
                      }
                      label="No"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={normalizedValue === 1}
                          onChange={() =>
                            setDataCase((prev) => {
                              const prevRaw = prev?.[item.field];
                              const prevNormalized =
                                prevRaw === null ||
                                prevRaw === undefined ||
                                prevRaw === ""
                                  ? null
                                  : Number(prevRaw);

                              return {
                                ...prev,
                                [item.field]: prevNormalized === 1 ? null : 1,
                              };
                            })
                          }
                        />
                      }
                      label="Sí"
                    />
                  </Grid>
                </Grid>
              );
            })}

            <Box mt={2} display="flex" justifyContent="flex-end">
              <CustomButtonPlusIcon
                icon={Autorenew}
                position="start"
                text={isMd ? "" : "Actualizar"}
                handleFunction={handleConfigPanel}
                variant="contained"
                size="small"
              />
            </Box>
          </Box>
        )}
      </Grid>

      {/* ---------------- SECCIÓN DERECHA: Eliminar expediente, Cambio de estado del expediente ---------------- */}
      <Grid item xs={12} lg={8} sx={{ p: { xs: "10px", md: "10px" } }}>
        {canChange && (
          <Box
            sx={{
              background: "rgba(255, 255, 255, 0.56)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              p: { xs: "10px" },
              borderRadius: "12px",
              border: "1px solid rgba(255, 255, 255, 0.55)",
              boxShadow: "0 6px 18px rgba(15,23,42,0.05)",
              height: "stretch",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Acciones del expediente
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* CAMBIO DE ESTADO */}
            <Grid container spacing={0.5}>
              <Grid item xs={9}>
                <Autocomplete
                  size="small"
                  value={revertCaseFlow}
                  onChange={(e, v) => v && setRevertCaseFlow(v)}
                  options={filteredOldStates}
                  getOptionLabel={(option) =>
                    option.status_name === "Seleccionar"
                      ? option.status_name
                      : `Pendiente ${option.status_name}`
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Retroceder estado del expediente"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3}>
                <CustomButtonPlusIcon
                  icon={Update}
                  position="start"
                  text="Cambiar"
                  fullWidth
                  handleFunction={() => handleOpenModal("status")}
                  sxProps={{ height: "100%" }}
                />
              </Grid>
            </Grid>

            {/* FECHA DE JUICIO */}
            <Grid container spacing={0.5} mt={1}>
              <Grid item xs={9}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="es"
                >
                  <DateTimePicker
                    label="Fecha y hora de juicio"
                    value={
                      dateTrialTime
                        ? dayjs(dateTrialTime)
                        : dataCase?.trial_date
                          ? dayjs(dataCase.trial_date)
                          : null
                    }
                    onChange={(newValue) => setDateTrialTime(newValue)}
                    ampm={false}
                    minutesStep={5}
                    slotProps={{
                      textField: { size: "small", fullWidth: true },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={3}>
                <CustomButtonPlusIcon
                  icon={Update}
                  handleFunction={handleChangeTrialDate}
                  text="Configurar"
                  position="start"
                  fullWidth
                  sxProps={{ height: "100%" }}
                />
              </Grid>
            </Grid>

            {/* ELIMINAR */}
            {canDelete && (
              <>
                <Divider sx={{ my: 2 }} />
                <CustomButtonPlusIcon
                  color="error"
                  icon={Delete}
                  text="Eliminar expediente"
                  handleFunction={() => handleOpenModal("eliminar")}
                  variant="outlined"
                  position="start"
                  fullWidth
                />
              </>
            )}
          </Box>
        )}
      </Grid>

      {/* ================= MODAL: Cambiar estado ================= */}

      <Dialog
        open={openModalStatus}
        onClose={
          activeStepStatus === stepsChangeStatus.length - 1
            ? null
            : handleCloseModal
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "420px", // altura personalizada
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#0f172a",
            color: "white",
            fontWeight: "700",
            mb: 3,
            p: 2,
          }}
        >
          Cambio de estado del Expediente
          <IconButton
            color="error"
            aria-label="cerrar"
            onClick={handleCloseModal}
            disabled={activeStepStatus === stepsChangeStatus.length - 1}
            sx={{ color: "white" }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Stepper activeStep={activeStepStatus} alternativeLabel>
              {stepsChangeStatus.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box mt={5}>
              {activeStepStatus === 0 && (
                <div>
                  <b>
                    Estás a punto de retroceder el Expediente a un estado
                    anterior.
                  </b>
                  <p>Si estás de acuerdo, da clic en siguiente.</p>

                  {isPendingSignatureSelected && (
                    <Box mt={2}>
                      <Alert severity="warning" sx={{ mb: 1.5 }}>
                        Se detectó que el expediente volverá a
                        {` ${revertCaseFlow?.status_name || "Pendiente firma"}`}
                        . Debes elegir si mantener la firma actual o reiniciar
                        el contrato.
                      </Alert>

                      <FormControl>
                        <FormLabel id="signature-action-group-label">
                          Decisión sobre contrato firmado
                        </FormLabel>
                        <RadioGroup
                          aria-labelledby="signature-action-group-label"
                          name="signature-action-group"
                          value={signatureAction}
                          onChange={(event) =>
                            setSignatureAction(event.target.value)
                          }
                        >
                          <FormControlLabel
                            value="keep"
                            control={<Radio />}
                            label="Mantener firma actual"
                          />
                          <FormControlLabel
                            value="reset"
                            control={<Radio />}
                            label="Reiniciar contrato"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  )}
                </div>
              )}
              {activeStepStatus === 1 && (
                <div style={{ position: "relative", height: "170px" }}>
                  {loading ? (
                    <>
                      <p style={{ color: "#0c1e35", textAlign: "center" }}>
                        <em>Actualizando expediente...</em>
                      </p>
                      <LoadingPage
                        isLoading={loading}
                        heightProp={"100px"}
                        widthProp={"100px"}
                      />
                    </>
                  ) : (
                    <Alert severity="success">
                      Expediente actualizado correctamente
                    </Alert>
                  )}
                </div>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ display: "block" }}>
          <Box mt={2} display="flex" justifyContent="space-between">
            <Button
              disabled={
                activeStepStatus === 0 || (!loading && activeStepStatus === 1)
              }
              onClick={handleBackStatus}
            >
              Atrás
            </Button>
            {activeStepStatus === stepsChangeStatus.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => handleFinish("status")}
                disabled={loading}
              >
                Finalizar
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => handleNext("status")}
                disabled={
                  activeStepStatus === 0 &&
                  isPendingSignatureSelected &&
                  !signatureAction
                }
              >
                Enviar
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* ================= MODAL: Eliminar expediente ================= */}
      <Dialog
        open={openModalDelete}
        onClose={
          activeStepDelete === stepsDelete.length - 1 ? null : handleCloseModal
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "550px", // altura personalizada
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#d32f2f",
            color: "white",
            fontWeight: "700",
            mb: 3,
            p: 2,
          }}
        >
          Eliminar expediente
          <IconButton
            aria-label="cerrar"
            onClick={handleCloseModal}
            disabled={activeStepDelete === stepsDelete.length - 1}
            sx={{ color: "white" }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box>
            <Stepper activeStep={activeStepDelete} alternativeLabel>
              {stepsDelete.map((label) => (
                <Step key={label}>
                  <StepLabel
                    slots={{
                      stepIcon: (props) => (
                        <div
                          style={{
                            backgroundColor: "#d32f2f",
                            color: "white",
                            borderRadius: "10px",
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.8rem",
                          }}
                        >
                          {props.icon}
                        </div>
                      ),
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box mt={5}>
              {activeStepDelete === 0 && (
                <div>
                  <Alert severity="warning">
                    Estás a punto de eliminar el Expediente completamente.
                  </Alert>
                  <Alert severity="info">
                    Clic en siguiente para continuar.
                  </Alert>
                </div>
              )}
              {activeStepDelete === 1 && (
                <div style={{ position: "relative", height: "170px" }}>
                  {loading ? (
                    <>
                      <p style={{ color: "#0c1e35", textAlign: "center" }}>
                        <em>Verificando datos vinculados al expediente...</em>
                      </p>
                      <LoadingPage
                        isLoading={loading}
                        heightProp={"100px"}
                        widthProp={"100px"}
                      />
                    </>
                  ) : Object.entries(docsInfo).length > 0 ? (
                    <div>
                      <Alert severity="warning">
                        <b
                          style={{
                            color: "rgb(102, 60, 0)",
                            marginRight: "10px",
                          }}
                        >
                          ¡Cuidado!
                        </b>
                        <span>
                          Se ha encontrado documentación asociada al caso.
                        </span>
                      </Alert>
                      <Alert severity="warning">
                        Al clicar en ELIMINAR EXPEDIENTE, toda la información
                        asociada al expediente será eliminada.
                      </Alert>
                      <Alert severity="info">
                        ¿Desea conservar la documentación del caso? En caso de
                        no hacerlo, se perderá para siempre. Por favor
                        seleccione una opción:
                      </Alert>
                      <FormControl
                        sx={{
                          "& .MuiFormControlLabel-label": { fontSize: 14 },
                          mt: 1,
                        }}
                      >
                        <RadioGroup
                          aria-labelledby="radioNewCaseStatus"
                          name="radio-buttons-group"
                          value={docsDecision} // <- controlado por estado
                          onChange={(event) =>
                            setDocsDecision(event.target.value)
                          }
                        >
                          <FormControlLabel
                            value="1"
                            control={<Radio />}
                            label="Conservar documentación."
                          />
                          <FormControlLabel
                            value="2"
                            control={<Radio />}
                            label="Eliminar documentación."
                          />
                        </RadioGroup>
                      </FormControl>
                    </div>
                  ) : (
                    <>
                      <Alert severity="success">
                        No se ha encontrado documentación asociada al
                        expediente, puede proseguir.
                      </Alert>
                      <Alert severity="warning">
                        Al clicar en ELIMINAR EXPEDIENTE, toda la información
                        asociada al expediente será eliminada.
                      </Alert>
                    </>
                  )}
                </div>
              )}
              {activeStepDelete === 2 && (
                <div style={{ position: "relative", height: "170px" }}>
                  {loading ? (
                    <>
                      <p style={{ color: "#0c1e35", textAlign: "center" }}>
                        <em>Eliminando expediente...</em>
                      </p>
                      <LoadingPage
                        isLoading={loading}
                        heightProp={"100px"}
                        widthProp={"100px"}
                      />
                    </>
                  ) : errorDoc ? (
                    <Alert severity="error">
                      Ha ocurrido un error al procesar la solicitud.
                    </Alert>
                  ) : (
                    <Box>
                      <Alert severity="success">
                        Expediente eliminado correctamente, Click en finalizar
                        para terminar el proceso.
                      </Alert>
                    </Box>
                  )}
                </div>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ display: "block" }}>
          <Box mt={2} display="flex" justifyContent="space-between">
            <Button
              disabled={!loading && activeStepDelete === 2}
              onClick={
                activeStepDelete === 0 ? handleCloseModal : handleBackDelete
              }
              variant="outlined"
              color="error"
            >
              {activeStepDelete === 0 ? "Cancelar" : "Atrás"}
            </Button>
            {activeStepDelete === stepsDelete.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => handleFinish("delete")}
                disabled={loading}
              >
                Finalizar
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => handleNext("eliminar")}
                color={"error"}
                disabled={
                  activeStepDelete === 1 &&
                  Object.entries(docsInfo).length > 0 &&
                  !docsDecision
                }
              >
                {activeStepDelete === 1 ? "Eliminar Expediente" : "Siguiente"}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ConfigControlSection;
